/**
 * PdfPreview - Preview component for PDF exports.
 * Shows a paper frame with correct aspect ratio, margins, header/footer.
 */

import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { Spinner, WarningCircle, Warning } from "@phosphor-icons/react";
import type {
  PdfExportOptions,
  PdfHeaderFooter,
  ReadabilityStatus,
} from "@/utils/export/types";
import {
  getPageDimensions,
  getMargins,
  getReservedSpace,
  hasHeaderFooterContent,
  mmToPx,
  formatPageSizeName,
} from "@/utils/export/pdfLayout";

// =============================================================================
// Types
// =============================================================================

export interface PdfPreviewProps {
  /** Data URL of the preview image */
  previewDataUrl: string | null;
  chartDimensions: { width: number; height: number };
  pdfOptions: PdfExportOptions;
  projectTitle?: string;
  projectAuthor?: string;
  /** Data URL of the project logo (when configured) */
  logoDataUrl?: string;
  isRendering: boolean;
  error: string | null;
  /** Effective zoom for timeline (for readability indicator) */
  effectiveZoom?: number;
  /** Readability status for labels */
  readabilityStatus?: ReadabilityStatus;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Build left/right text arrays for a header or footer section.
 */
function buildSectionContent(
  section: PdfHeaderFooter,
  projectTitle: string | undefined,
  projectAuthor: string | undefined,
  formattedDate: string,
  logoDataUrl: string | undefined
): { left: string[]; right: string[]; showLogo: boolean } {
  const left: string[] = [];
  const right: string[] = [];
  const showLogo = section.showLogo && !!logoDataUrl;

  if (section.showProjectName && projectTitle) {
    left.push(projectTitle);
  }
  if (section.showAuthor && projectAuthor) {
    right.push(projectAuthor);
  }
  if (section.showExportDate) {
    right.push(formattedDate);
  }

  return { left, right, showLogo };
}

/**
 * Calculate scale factor when fitting chart into the PDF content area.
 * Returns a value in [0, 1] — 1 means the chart fits without scaling.
 */
function calculateScaleFactor(
  chartWidth: number,
  chartHeight: number,
  contentWidthMm: number,
  chartAreaHeightMm: number
): number {
  if (chartWidth <= 0 || chartHeight <= 0) return 1;
  // Clamp to zero — extreme custom margins could make the area negative
  const safeHeightMm = Math.max(chartAreaHeightMm, 0);
  if (contentWidthMm <= 0 || safeHeightMm <= 0) return 0;

  const chartAreaWidthPx = mmToPx(contentWidthMm);
  const chartAreaHeightPx = mmToPx(safeHeightMm);
  const scaleX = chartAreaWidthPx / chartWidth;
  const scaleY = chartAreaHeightPx / chartHeight;
  return Math.min(scaleX, scaleY, 1);
}

// =============================================================================
// Constants
// =============================================================================

/** Warn when chart content must be scaled below this factor to fit the page */
const SCALE_WARNING_THRESHOLD = 0.5;

/** Middle dot separator for header/footer text items */
const DOT_SEPARATOR = " \u00B7 ";

// =============================================================================
// Sub-components
// =============================================================================

interface SectionStripProps {
  left: string[];
  right: string[];
  border: "top" | "bottom";
  logoDataUrl?: string;
  /** Strip height as a fraction of the content area (0–1), matching PDF reserved space */
  heightFraction: number;
}

/** Dot-separated row in the header or footer strip */
function SectionStrip({
  left,
  right,
  border,
  logoDataUrl,
  heightFraction,
}: SectionStripProps): JSX.Element {
  const isHeader = border === "bottom";
  const borderClass = isHeader
    ? "border-b border-slate-300"
    : "border-t border-slate-300";
  // Header: content near bottom border (separator). Footer: near top border.
  // Fixed padding creates a small visual gap from the separator line,
  // approximating the PDF's 2mm/10mm spacing at preview scale.
  const alignClass = isHeader ? "items-end pb-0.5" : "items-start pt-0.5";
  return (
    <div
      className={`flex justify-between px-1 ${borderClass} ${alignClass} shrink-0`}
      style={{ height: `${heightFraction * 100}%` }}
    >
      <span
        className={`text-[6px] leading-none text-slate-600 flex ${isHeader ? "items-end" : "items-start"} gap-0.5 min-w-0`}
      >
        {logoDataUrl && (
          <img
            src={logoDataUrl}
            alt=""
            className="w-auto object-contain shrink-0"
            style={{ height: "1.5em" }}
          />
        )}
        <span className="truncate">{left.join(DOT_SEPARATOR)}</span>
      </span>
      <span className="text-[6px] leading-none text-slate-600 truncate">
        {right.join(DOT_SEPARATOR)}
      </span>
    </div>
  );
}

type WarningLevel = "warning" | "critical";

interface WarningBannerProps {
  level: WarningLevel;
  icon: PhosphorIcon;
  message: string;
}

const WARNING_STYLES: Record<
  WarningLevel,
  { bg: string; icon: string; text: string }
> = {
  warning: {
    bg: "bg-amber-50 border border-amber-200",
    icon: "text-amber-600",
    text: "text-amber-700",
  },
  critical: {
    bg: "bg-red-50 border border-red-200",
    icon: "text-red-600",
    text: "text-red-700",
  },
};

/** Reusable warning/critical alert banner */
function WarningBanner({
  level,
  icon: Icon,
  message,
}: WarningBannerProps): JSX.Element {
  const styles = WARNING_STYLES[level];
  return (
    <div className={`flex items-center gap-2.5 px-4 py-3 rounded ${styles.bg}`}>
      <Icon
        size={16}
        weight="fill"
        aria-hidden="true"
        className={styles.icon}
      />
      <span className={`text-xs font-semibold ${styles.text}`}>{message}</span>
    </div>
  );
}

interface PdfPreviewInfoProps {
  effectiveZoom: number | undefined;
  pdfOptions: PdfExportOptions;
  pageDims: { width: number; height: number };
  scaleFactor: number;
  readabilityStatus: ReadabilityStatus | undefined;
}

/** Info panel below the paper frame showing zoom, page size, and warnings */
function PdfPreviewInfo({
  effectiveZoom,
  pdfOptions,
  pageDims,
  scaleFactor,
  readabilityStatus,
}: PdfPreviewInfoProps): JSX.Element {
  const scalePercent = Math.round(scaleFactor * 100);

  // Extract warning-level readability so TypeScript narrows without `as` cast
  const readabilityWarning =
    readabilityStatus && readabilityStatus.level !== "good"
      ? { level: readabilityStatus.level, message: readabilityStatus.message }
      : null;

  return (
    <div className="mt-4 space-y-2">
      {/* Single row with dot separators */}
      <div className="text-xs text-slate-600">
        <span className="font-medium text-slate-900">
          {effectiveZoom !== undefined
            ? `${Math.round(effectiveZoom * 100)}%`
            : "\u2014"}
        </span>
        {" zoom"}
        <span className="mx-1.5 text-slate-300">&middot;</span>
        <span className="font-medium text-slate-900">
          {formatPageSizeName(pdfOptions.pageSize)}{" "}
          {pdfOptions.orientation === "landscape" ? "Landscape" : "Portrait"}
        </span>
        <span className="mx-1.5 text-slate-300">&middot;</span>
        <span className="font-medium text-slate-900">
          {pageDims.width}&times;{pageDims.height}
        </span>
        {" mm"}
      </div>

      {/* Readability Indicator - only show warnings/critical */}
      {readabilityWarning && (
        <WarningBanner
          level={readabilityWarning.level}
          icon={Warning}
          message={readabilityWarning.message}
        />
      )}

      {/* Scale Warning - if content needs significant scaling to fit page */}
      {scaleFactor < SCALE_WARNING_THRESHOLD && (
        <WarningBanner
          level="warning"
          icon={WarningCircle}
          message={`Content scaled to ${scalePercent}% \u2014 consider larger page size`}
        />
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * PdfPreview component for PDF export preview.
 * Shows a paper frame with header/footer and chart content.
 */
export function PdfPreview({
  previewDataUrl,
  chartDimensions,
  pdfOptions,
  projectTitle,
  projectAuthor,
  logoDataUrl,
  isRendering,
  error,
  effectiveZoom,
  readabilityStatus,
}: PdfPreviewProps): JSX.Element {
  // Use canonical layout utilities — single source of truth with pdfExport.ts
  const pageDims = getPageDimensions(pdfOptions);
  const margins = getMargins(pdfOptions);

  const aspectRatio = pageDims.width / pageDims.height;
  const contentWidthMm = pageDims.width - margins.left - margins.right;
  const contentHeightMm = pageDims.height - margins.top - margins.bottom;

  const hasHeader = hasHeaderFooterContent(pdfOptions.header);
  const hasFooter = hasHeaderFooterContent(pdfOptions.footer);
  const headerHeightMm = getReservedSpace(pdfOptions.header);
  const footerHeightMm = getReservedSpace(pdfOptions.footer);
  const chartAreaHeightMm = contentHeightMm - headerHeightMm - footerHeightMm;

  const scaleFactor = calculateScaleFactor(
    chartDimensions.width,
    chartDimensions.height,
    contentWidthMm,
    chartAreaHeightMm
  );

  const formattedDate = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const headerContent = buildSectionContent(
    pdfOptions.header,
    projectTitle,
    projectAuthor,
    formattedDate,
    logoDataUrl
  );
  const footerContent = buildSectionContent(
    pdfOptions.footer,
    projectTitle,
    projectAuthor,
    formattedDate,
    logoDataUrl
  );

  // Margin padding as percentages of page dimensions for responsive scaling
  const topPct = (margins.top / pageDims.height) * 100;
  const rightPct = (margins.right / pageDims.width) * 100;
  const bottomPct = (margins.bottom / pageDims.height) * 100;
  const leftPct = (margins.left / pageDims.width) * 100;
  const marginPadding = `${topPct}% ${rightPct}% ${bottomPct}% ${leftPct}%`;

  return (
    <div className="flex flex-col h-full">
      {/* Preview Header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-1">
          PDF Preview
        </h3>
        <p className="text-xs text-slate-500">Page layout visualization</p>
      </div>

      {/* Paper Frame Container */}
      <div className="flex-1 flex items-center justify-center p-2">
        <div
          className="bg-white border border-slate-300 shadow-sm relative overflow-hidden"
          style={{
            aspectRatio: `${aspectRatio}`,
            maxWidth: "100%",
            maxHeight: "100%",
            width: aspectRatio >= 1 ? "100%" : "auto",
            height: aspectRatio < 1 ? "100%" : "auto",
          }}
        >
          {/* Margin visualization */}
          <div
            className="absolute inset-0 flex flex-col"
            style={{ padding: marginPadding }}
          >
            {/* Margin border (dashed) */}
            <div className="absolute inset-0 border border-dashed border-slate-300 pointer-events-none" />

            {/* Content area */}
            <div className="flex flex-col h-full">
              {hasHeader && (
                <SectionStrip
                  left={headerContent.left}
                  right={headerContent.right}
                  border="bottom"
                  logoDataUrl={headerContent.showLogo ? logoDataUrl : undefined}
                  heightFraction={headerHeightMm / contentHeightMm}
                />
              )}

              {/* Chart Content Area */}
              <div className="flex-1 flex items-start justify-center relative min-h-0 overflow-hidden">
                {isRendering && (
                  <div
                    className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-10"
                    aria-live="polite"
                    role="status"
                  >
                    <Spinner
                      size={20}
                      aria-hidden="true"
                      className="animate-spin text-brand-600"
                      weight="regular"
                    />
                    <p className="text-[8px] text-slate-600 mt-2">
                      Rendering...
                    </p>
                  </div>
                )}

                {error && !isRendering && (
                  <div
                    className="absolute inset-0 bg-white flex flex-col items-center justify-center z-10 p-2"
                    role="alert"
                  >
                    <WarningCircle
                      size={16}
                      aria-hidden="true"
                      className="text-red-500"
                      weight="fill"
                    />
                    <p className="text-[7px] text-red-700 mt-1 text-center">
                      {error}
                    </p>
                  </div>
                )}

                {!error && previewDataUrl && (
                  <img
                    src={previewDataUrl}
                    alt="Export preview"
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                  />
                )}

                {!previewDataUrl && !isRendering && !error && (
                  <div className="flex items-center justify-center">
                    <div className="w-3/4 h-1/2 bg-slate-100 rounded flex items-center justify-center">
                      <span className="text-[7px] text-slate-400">
                        Chart content
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {hasFooter && (
                <SectionStrip
                  left={footerContent.left}
                  right={footerContent.right}
                  border="top"
                  logoDataUrl={footerContent.showLogo ? logoDataUrl : undefined}
                  heightFraction={footerHeightMm / contentHeightMm}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <PdfPreviewInfo
        effectiveZoom={effectiveZoom}
        pdfOptions={pdfOptions}
        pageDims={pageDims}
        scaleFactor={scaleFactor}
        readabilityStatus={readabilityStatus}
      />
    </div>
  );
}
